/**
 * Utilitários para lógica e cálculos de paginação de tabelas e listas
 */

export interface PaginationMeta {
  currentPage: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  startIndex: number;
  endIndex: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

export function calculatePagination(
  totalItems: number,
  currentPage: number = 1,
  pageSize: number = 15
): PaginationMeta {
  const safePageSize = pageSize > 0 ? pageSize : 15;
  const safeTotalItems = Math.max(0, totalItems);
  const totalPages = Math.max(1, Math.ceil(safeTotalItems / safePageSize));

  // Clampa página entre 1 e totalPages
  const validPage = Math.max(1, Math.min(currentPage, totalPages));

  const startIndex = safeTotalItems === 0 ? 0 : (validPage - 1) * safePageSize;
  const endIndex = Math.min(startIndex + safePageSize, safeTotalItems);

  return {
    currentPage: validPage,
    pageSize: safePageSize,
    totalItems: safeTotalItems,
    totalPages,
    startIndex,
    endIndex,
    hasNextPage: validPage < totalPages,
    hasPrevPage: validPage > 1,
  };
}

export function paginateList<T>(
  items: T[],
  currentPage: number = 1,
  pageSize: number = 15
): { items: T[]; meta: PaginationMeta } {
  const meta = calculatePagination(items.length, currentPage, pageSize);
  const paginatedItems = items.slice(meta.startIndex, meta.endIndex);
  return {
    items: paginatedItems,
    meta,
  };
}

export function getVisiblePageNumbers(
  currentPage: number,
  totalPages: number,
  maxVisibleButtons: number = 7
): (number | string)[] {
  if (totalPages <= maxVisibleButtons) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages: (number | string)[] = [];
  pages.push(1);

  if (currentPage > 3) {
    pages.push("...");
  }

  const start = Math.max(2, currentPage - 1);
  const end = Math.min(totalPages - 1, currentPage + 1);

  for (let i = start; i <= end; i++) {
    pages.push(i);
  }

  if (currentPage < totalPages - 2) {
    pages.push("...");
  }

  pages.push(totalPages);
  return pages;
}
